import React, { useRef, useState } from 'react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useLanguage } from '@/context/LanguageContext';
import { restaurantInfo } from '@/data/menuData';
import { Download, Printer, ArrowLeft, QrCode, Phone, MapPin, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const QRCodePage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const qrRef = useRef(null);
  const [qrSize, setQrSize] = useState(256);

  // Production URL for QR code - always use the permanent domain
  const menuUrl = 'https://www.gomsurestoran.com';

  const texts = {
    tr: {
      title: 'QR Menü Kodu',
      subtitle: 'Müşterileriniz bu kodu okutarak menünüze ulaşabilir',
      download: 'PNG İndir',
      downloadSVG: 'SVG İndir',
      print: 'Yazdır',
      back: 'Geri Dön',
      scanText: 'Menü için QR kodu okutun',
      size: 'Boyut',
      small: 'Küçük',
      medium: 'Orta',
      large: 'Büyük',
      xlarge: 'Çok Büyük',
      address: 'Adres',
      phone: 'Telefon',
      hours: 'Çalışma Saatleri',
      everyDay: 'Her Gün',
    },
    en: {
      title: 'QR Menu Code',
      subtitle: 'Your customers can access your menu by scanning this code',
      download: 'Download PNG',
      downloadSVG: 'Download SVG',
      print: 'Print',
      back: 'Go Back',
      scanText: 'Scan QR code for menu',
      size: 'Size',
      small: 'Small',
      medium: 'Medium',
      large: 'Large',
      xlarge: 'Extra Large',
      address: 'Address',
      phone: 'Phone',
      hours: 'Opening Hours',
      everyDay: 'Every Day',
    },
  };

  const t = texts[language];

  const sizes = [
    { value: 180, label: t.small },
    { value: 256, label: t.medium },
    { value: 350, label: t.large },
    { value: 450, label: t.xlarge },
  ];

  const downloadQRCode = (format = 'png') => {
    if (format === 'png') {
      const canvas = document.getElementById('qr-canvas');
      if (canvas) {
        const url = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `gomsu-restoran-qr-menu.png`;
        link.href = url;
        link.click();
      }
    } else if (format === 'svg') {
      const svg = document.getElementById('qr-svg');
      if (svg) {
        const svgData = new XMLSerializer().serializeToString(svg);
        const blob = new Blob([svgData], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `gomsu-restoran-qr-menu.svg`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      }
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.back}
        </Button>
        
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <QrCode className="w-8 h-8 text-primary" />
            <h1 className="font-display text-3xl text-foreground">{t.title}</h1>
          </div>
          <p className="text-muted-foreground">{t.subtitle}</p>
        </div>
      </div>

      {/* QR Code Card - Printable Area */}
      <div className="max-w-md mx-auto print:max-w-full" ref={qrRef}>
        <Card className="bg-white p-8 rounded-2xl shadow-elegant print:shadow-none print:border-2 print:border-gray-300">
          {/* Restaurant Info */}
          <div className="text-center mb-6">
            {/* Logo */}
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-gray-800 to-black flex items-center justify-center">
              <span className="font-display text-xl text-amber-500">gomşu</span>
            </div>
            <h2 className="font-display text-2xl text-gray-900 mb-1">
              {restaurantInfo.name}
            </h2>
            <p className="text-gray-500 text-sm">Restoran & Adana Kebapçısı</p>
          </div>

          {/* QR Code */}
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-white rounded-xl border-2 border-amber-500/30">
              {/* Canvas for PNG download */}
              <QRCodeCanvas
                id="qr-canvas"
                value={menuUrl}
                size={qrSize}
                level="H"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#1a1a1a"
                style={{ display: 'block' }}
              />
              {/* Hidden SVG for SVG download */}
              <div style={{ display: 'none' }}>
                <QRCodeSVG
                  id="qr-svg"
                  value={menuUrl}
                  size={qrSize}
                  level="H"
                  includeMargin={true}
                  bgColor="#ffffff"
                  fgColor="#1a1a1a"
                />
              </div>
            </div>
          </div>

          {/* Scan Text */}
          <p className="text-center text-gray-600 font-medium mb-6 text-lg">
            📱 {t.scanText}
          </p>

          {/* Restaurant Details */}
          <div className="space-y-3 text-sm text-gray-600 border-t border-gray-200 pt-6">
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <span>{restaurantInfo.address}</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-amber-600 shrink-0" />
              <span>{restaurantInfo.phone}</span>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-amber-600 shrink-0" />
              <span>{t.everyDay}: {restaurantInfo.hours.open} - {restaurantInfo.hours.close}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Controls - Hidden when printing */}
      <div className="max-w-md mx-auto mt-8 space-y-6 print:hidden">
        {/* Size Selection */}
        <div>
          <label className="block text-sm text-muted-foreground mb-3">{t.size}</label>
          <div className="grid grid-cols-4 gap-2">
            {sizes.map((size) => (
              <Button
                key={size.value}
                variant={qrSize === size.value ? 'default' : 'outline'}
                onClick={() => setQrSize(size.value)}
                className={`text-xs ${
                  qrSize === size.value
                    ? 'gradient-gold text-primary-foreground'
                    : 'border-border/50 text-foreground hover:border-primary/50'
                }`}
              >
                {size.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-3 gap-3">
          <Button
            onClick={() => downloadQRCode('png')}
            className="gradient-gold text-primary-foreground py-6 rounded-xl flex flex-col items-center gap-1"
          >
            <Download className="w-5 h-5" />
            <span className="text-xs">{t.download}</span>
          </Button>
          <Button
            onClick={() => downloadQRCode('svg')}
            variant="outline"
            className="py-6 rounded-xl flex flex-col items-center gap-1 border-primary/50 text-foreground hover:bg-primary/10"
          >
            <Download className="w-5 h-5" />
            <span className="text-xs">{t.downloadSVG}</span>
          </Button>
          <Button
            onClick={handlePrint}
            variant="outline"
            className="py-6 rounded-xl flex flex-col items-center gap-1 border-border/50 text-foreground hover:bg-muted"
          >
            <Printer className="w-5 h-5" />
            <span className="text-xs">{t.print}</span>
          </Button>
        </div>

        {/* URL Display */}
        <div className="bg-muted/50 rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Menü URL:</p>
          <p className="text-sm text-foreground font-mono break-all">{menuUrl}</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:max-w-full {
            max-width: 100% !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:border-2 {
            border-width: 2px !important;
          }
          .print\\:border-gray-300 {
            border-color: #d1d5db !important;
          }
        }
      `}</style>
    </div>
  );
};

export default QRCodePage;
